import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { MessageSquare, Home } from 'lucide-react'

/**
 * Public description of how Photodrop obtains consent before sending SMS.
 * Referenced as the opt-in evidence URL on our toll-free verification with Twilio,
 * so the flow described here must match what the invite UI actually does.
 */
export default function SmsConsentPage() {
  return (
    <div className="min-h-screen bg-muted/30 p-4 py-12">
      <div className="max-w-2xl mx-auto bg-card text-card-foreground rounded-lg border shadow-sm p-8">
        <MessageSquare className="h-12 w-12 text-primary" />
        <h1 className="mt-6 text-3xl font-bold">SMS Messaging Policy</h1>
        <p className="mt-2 text-muted-foreground">
          How Photodrop uses text messages, and how consent is collected.
        </p>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">What Photodrop is</h2>
          <p className="text-muted-foreground">
            Photodrop lets someone organizing an event — a wedding, a party, a trip — collect photos
            from the people who were there. The organizer creates a "drop" and invites specific
            people they personally know to upload their photos to it.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">When we send a text</h2>
          <p className="text-muted-foreground">
            The only text messages Photodrop sends are invitations to contribute photos to a
            specific drop, plus at most one reminder if the invitation goes unanswered. Each message
            is sent one-to-one, initiated manually by the organizer for a person they invited.
          </p>
          <p className="text-muted-foreground">
            We do not send marketing, promotional, or bulk messages, and we never sell or share
            phone numbers.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">How consent is obtained</h2>
          <p className="text-muted-foreground">
            Consent is collected directly by the organizer, in person or by conversation, before any
            message is sent. The invitee gives their mobile number to the organizer specifically so
            they can be sent a link to upload photos.
          </p>
          <p className="text-muted-foreground">
            Before Photodrop will send the text, the organizer must check a box in the invite form
            affirming the following:
          </p>
          <blockquote className="border-l-4 border-primary pl-4 py-2 italic">
            "I confirm this person agreed to receive a text message from me about this photo drop.
            They can reply STOP to opt out."
          </blockquote>
          <p className="text-muted-foreground">
            The invitation cannot be sent unless this box is checked. There is no mailing list, no
            purchased contacts, and no automated or bulk opt-in.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Example messages</h2>
          <div className="rounded-md bg-muted p-4 space-y-3 text-sm">
            <p>
              Jackson invited you to add photos to "Sarah's Wedding" on Photodrop:
              https://photodrop.dev/c/aB3cD4eF Reply STOP to opt out.
            </p>
            <p>
              Reminder: you were invited to share photos on Photodrop. Reply STOP to opt out.
            </p>
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Opting out</h2>
          <p className="text-muted-foreground">
            Reply <strong>STOP</strong> to any message to opt out and receive no further texts.
            Reply <strong>HELP</strong> for assistance, or email{' '}
            <a className="text-primary underline" href="mailto:support@photodrop.dev">
              support@photodrop.dev
            </a>
            . Message and data rates may apply.
          </p>
        </section>

        <div className="mt-10">
          <Button asChild>
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              Back to Photodrop
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
